import React, { useState, useEffect } from 'react';
import { Partner, CommonPartner } from 'types';
import styles from './EditPartnerSettings.module.scss';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import { Button } from '../../../../../../components/ui/button';
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	CircularProgress,
	Autocomplete,
} from '@mui/material';
import * as Yup from 'yup';
import {
	getCommonPartners,
	updatePartner,
	getPartnerTypes,
	PartnerType,
	getPartnerListWorkplaces,
	createPartnerListWorkplace,
	deletePartnerListWorkplace,
} from 'core/API/partners';
import { GetAllWorkplaceList } from 'core/API/workplaces';

interface Workplace {
	id: number;
	name: string;
	level: number;
	selected: boolean;
	'@id'?: string;
	assignmentId?: number; // ID of the partner_list_workplace association
}

interface EditPartnerSettingsProps {
	user: Partner; // This will be the partner data
	handleBackClick?: () => void;
}

const EditPartnerSettings: React.FC<EditPartnerSettingsProps> = ({
	user: partner,
	handleBackClick,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const [activateDialogOpen, setActivateDialogOpen] = useState(false);
	const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const [workplaceUnassignDialogOpen, setWorkplaceUnassignDialogOpen] = useState(false);
	const [pendingWorkplaceId, setPendingWorkplaceId] = useState<number | null>(null);
	const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
	const [loadingWorkplaces, setLoadingWorkplaces] = useState(false);
	const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
	const [commonPartners, setCommonPartners] = useState<CommonPartner[]>([]);
	const [_loadingCommonPartners, setLoadingCommonPartners] = useState(false);
	const [partnerTypes, setPartnerTypes] = useState<PartnerType[]>([]);
	const [_loadingPartnerTypes, setLoadingPartnerTypes] = useState(false);

	// Fetch common partners, partner types, workplaces, and existing assignments on mount
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoadingCommonPartners(true);
				setLoadingPartnerTypes(true);
				setLoadingWorkplaces(true);

				const [partners, types, workplacesData] = await Promise.all([
					getCommonPartners(),
					getPartnerTypes(),
					GetAllWorkplaceList(),
				]);

				setCommonPartners(partners.filter((p) => p.available));
				setPartnerTypes(types);

				// Fetch existing workplace assignments for this partner
				let assignments = [];
				if (partner?.id) {
					assignments = await getPartnerListWorkplaces({ partnerList: partner.id });
				}

				// Map workplaces and mark selected ones based on existing assignments
				const assignedWorkplaceIds = new Set(
					assignments
						.map((a: any) => {
							const match = a.workplace.match(/\/(\d+)$/);
							return match ? Number(match[1]) : null;
						})
						.filter(Boolean)
				);

				setWorkplaces(
					(Object.values(workplacesData) || []).map((w) => {
						const isAssigned = assignedWorkplaceIds.has(w.id);
						const assignment = assignments.find((a: any) => a.workplace.includes(`/${w.id}`));
						return {
							id: w.id,
							name: w.name,
							level: 1, // You can enhance this with hierarchy logic if needed
							selected: isAssigned,
							'@id': `/api/workplaces/${w.id}`,
							assignmentId: assignment?.id,
						};
					})
				);

				// Initialize parameter values from partner's parameters
				if (partner?.parameters) {
					const params = Array.isArray(partner.parameters) && partner.parameters.length > 0
						? partner.parameters[0]
						: partner.parameters;
					if (typeof params === 'object' && params !== null) {
						const initialParams: Record<string, string> = {};
						Object.keys(params).forEach((key) => {
							initialParams[key] = params[key] || '';
						});
						setParameterValues(initialParams);
					}
				}
			} catch (error: any) {
				console.error('Failed to fetch data:', error);
				setApiError('Failed to load options');
			} finally {
				setLoadingCommonPartners(false);
				setLoadingPartnerTypes(false);
				setLoadingWorkplaces(false);
			}
		};
		fetchData();
	}, [partner?.id, partner?.parameters]);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialFormikValues = React.useMemo(
		() => ({
			label: partner?.label || '',
			partnerId: partner?.partnerId || '',
			partnerTypeId: '',
			active: partner?.active ?? false,
		}),
		[partner?.label, partner?.partnerId, partner?.active]
	);

	const validationSchema = Yup.object().shape({
		label: Yup.string().required('Partner name is required'),
		partnerId: Yup.string().required('Partner is required'),
		partnerTypeId: Yup.string().required('Partner type is required'),
		active: Yup.boolean(),
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setApiError(null);
				setApiSuccess(null);

				const selectedPartner = commonPartners.find((p) => p.id === Number(values.partnerId));
				const selectedPartnerType = partnerTypes.find(
					(pt) => pt.id === Number(values.partnerTypeId)
				);

				// Ensure parameters is an object, using parameterValues if available
				const getParameters = () => {
					if (Object.keys(parameterValues).length > 0) {
						return parameterValues;
					}
					if (selectedPartner) {
						const params = selectedPartner.parameters;
						return Array.isArray(params) && params.length > 0 ? params[0] : (params || {});
					}
					const params = partner.parameters;
					return Array.isArray(params) && params.length > 0 ? params[0] : (params || {});
				};

				// Prepare the payload for updating the partner_list
				const updatePayload = {
					label: values.label,
					partnerId: selectedPartner ? selectedPartner.id : partner.partnerId,
					type: selectedPartnerType ? selectedPartnerType['@id'] : partner.type || '',
					parameters: getParameters(),
					active: values.active,
					deleted: partner.deleted || false,
					created_at: partner.created_at,
					updated_at: new Date().toISOString(),
				};

				await updatePartner(partner.id, updatePayload);

				// Handle workplace assignments
				if (partner['@id']) {
					// Find workplaces that need to be added (newly selected)
					const workplacesToAdd = workplaces.filter((w) => w.selected && !w.assignmentId);
					// Find workplaces that need to be removed (previously selected, now unselected)
					const workplacesToRemove = workplaces.filter((w) => !w.selected && w.assignmentId);

					// Create new associations
					if (workplacesToAdd.length > 0) {
						await Promise.all(
							workplacesToAdd.map((workplace) =>
								createPartnerListWorkplace({
									partnerList: partner['@id'] || '',
									workplace: workplace['@id'] || `/api/workplaces/${workplace.id}`,
								})
							)
						);
					}

					// Delete removed associations
					if (workplacesToRemove.length > 0) {
						await Promise.all(
							workplacesToRemove.map((workplace) =>
								workplace.assignmentId
									? deletePartnerListWorkplace(workplace.assignmentId)
									: Promise.resolve()
							)
						);
					}
				}

				setApiSuccess('Partner updated successfully!');

				// Navigate back after a short delay
				setTimeout(() => {
					handleBackClick?.();
				}, 1500);
			} catch (error: any) {
				setApiError(error.message || 'Failed to update partner');
			} finally {
				setSubmitting(false);
			}
		},
	});

	// Update partnerTypeId when partner types are loaded and we have partner.type
	useEffect(() => {
		if (partnerTypes.length > 0 && partner?.type) {
			const matchingType = partnerTypes.find((pt) => pt['@id'] === partner.type);
			if (matchingType && Number(formik.values.partnerTypeId) !== matchingType.id) {
				formik.setFieldValue('partnerTypeId', matchingType.id);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [partnerTypes, partner?.type]);

	const handleActivateToggle = () => {
		// If trying to activate an inactive partner, show confirmation dialog
		if (!formik.values.active) {
			setActivateDialogOpen(true);
		} else {
			// If deactivating an active partner, show confirmation dialog
			setDeactivateDialogOpen(true);
		}
	};

	const confirmActivatePartner = () => {
		formik.setFieldValue('active', true);
		setActivateDialogOpen(false);

		// Auto-assign organization (level 1) workplace when activating partner
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.level === 1 ? { ...workplace, selected: true } : workplace
			)
		);

		setApiSuccess('Partner activated successfully! Organization workplace automatically assigned.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const confirmDeactivatePartner = () => {
		formik.setFieldValue('active', false);
		setDeactivateDialogOpen(false);

		// Clear all workplace assignments when deactivating partner
		setWorkplaces((prev) => prev.map((workplace) => ({ ...workplace, selected: false })));

		setApiSuccess('Partner deactivated successfully! All workplace assignments removed.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const handleWorkplaceToggle = (workplaceId: number) => {
		const workplace = workplaces.find((w) => w.id === workplaceId);

		// If workplace is currently selected and user wants to unassign it, show confirmation
		if (workplace && workplace.selected) {
			setPendingWorkplaceId(workplaceId);
			setWorkplaceUnassignDialogOpen(true);
		} else {
			// If assigning workplace, allow immediate change
			toggleWorkplaceAssignment(workplaceId);
		}
	};

	const toggleWorkplaceAssignment = (workplaceId: number) => {
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.id === workplaceId ? { ...workplace, selected: !workplace.selected } : workplace
			)
		);
	};

	const handleSelectAllWorkplaces = (checked: boolean) => {
		setWorkplaces((prev) =>
			prev.map((workplace) => ({ ...workplace, selected: checked }))
		);
	};

	const allWorkplacesSelected = workplaces.length > 0 && workplaces.every((w) => w.selected);
	const someWorkplacesSelected = workplaces.some((w) => w.selected) && !allWorkplacesSelected;

	const confirmWorkplaceUnassign = () => {
		if (pendingWorkplaceId) {
			toggleWorkplaceAssignment(pendingWorkplaceId);
			const workplace = workplaces.find((w) => w.id === pendingWorkplaceId);
			setApiSuccess(`${workplace?.name} unassigned successfully!`);
			setTimeout(() => setApiSuccess(null), 2000);
		}
		setWorkplaceUnassignDialogOpen(false);
		setPendingWorkplaceId(null);
	};

	if (!partner) {
		return (
			<div className={styles.loading}>
				<CircularProgress size={20} color="inherit" />
			</div>
		);
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	return (
		<div className={styles.editPartnerSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				{/* Header with Back and Save buttons */}
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Grid>
							<Button className={styles.backButton} onClick={handleSubmit}>
								Save
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Partner Details</h2>
				</Grid>
			</Grid>

			{/* API Feedback */}
			{apiSuccess && <div className={styles.successMessage}>{apiSuccess}</div>}
			{apiError && <div className={styles.errorMessage}>{apiError}</div>}

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Partner Selection (Read-only) */}
				<Autocomplete
					options={commonPartners}
					getOptionLabel={(option) => option.label}
					value={commonPartners.find((p) => p.id === Number(formik.values.partnerId)) || null}
					disabled
					renderInput={(params) => (
						<TextField {...params} label="Partner *" margin="normal" disabled />
					)}
				/>

				{/* Partner Type Autocomplete */}
				<Autocomplete
					options={partnerTypes}
					getOptionLabel={(option) => option.label}
					value={partnerTypes.find((pt) => pt.id === Number(formik.values.partnerTypeId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerTypeId', newValue?.id || '');
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner Type *"
							margin="normal"
							error={formik.touched.partnerTypeId && Boolean(formik.errors.partnerTypeId)}
							helperText={formik.touched.partnerTypeId && formik.errors.partnerTypeId}
						/>
					)}
				/>

				{/* Dynamic Parameter Fields */}
				{Object.keys(parameterValues).length > 0 && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
						}}
					>
						<Box sx={{ 
							fontWeight: 400, 
							fontSize: '16px',
							color: 'rgb(0 0 0 / 87%)',
							marginBottom: 2 
						}}>
							Partner Configuration Parameters
						</Box>
						{Object.keys(parameterValues).map((key) => (
							<TextField
								key={key}
								label={key}
								value={parameterValues[key]}
								onChange={(e) => {
									setParameterValues(prev => ({
										...prev,
										[key]: e.target.value
									}));
								}}
								margin="normal"
								fullWidth
							/>
						))}
					</Box>
				)}

				{/* Partner Status */}
				<Box
					sx={{
						marginY: 2,
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.active}
								onChange={handleActivateToggle}
								sx={{
								color: '#446388',
								'&.Mui-checked': {
									color: '#446388',
									},
								}}
							/>
						}
						label={'Active Partner'}
					/>
				</Box>
				{/* Workplace Assignment Box - Only show if partner is active */}
				{formik.values.active && (
					<Box
						sx={{
							border: '1px solid #ccc',
							borderRadius: 2,
							padding: 2,
							marginY: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: 2,
							}}
						>
							<Box
								sx={{
									fontWeight: 400,
									fontSize: '16px',
									color: 'rgb(0 0 0 / 87%)',
								}}
							>
								Workplace Assignment
							</Box>
							{!loadingWorkplaces && workplaces.length > 0 && (
								<FormControlLabel
									control={
										<Checkbox
											checked={allWorkplacesSelected}
											indeterminate={someWorkplacesSelected}
											onChange={(e) => handleSelectAllWorkplaces(e.target.checked)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
												'&.MuiCheckbox-indeterminate': {
													color: '#446388',
												},
											}}
										/>
									}
									label="Select All"
								/>
							)}
						</Box>
						{loadingWorkplaces ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
								<CircularProgress size={24} />
							</Box>
						) : (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
								{workplaces.map((workplace) => (
									<Box
										key={workplace.id}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '4px 8px',
											borderRadius: 1,
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.04)',
											},
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<span style={{ marginLeft: workplace.level * 20 }}>
												{workplace.level > 1 && '└─ '}
												{workplace.name}
											</span>
											{/* {workplace.selected && <Chip label="Assigned" size="small" color="primary" />} */}
										</Box>
										<Checkbox
											checked={workplace.selected}
											onChange={() => handleWorkplaceToggle(workplace.id)}
											sx={{
											color: '#446388',
											'&.Mui-checked': {
												color: '#446388',
												},
											}}
										/>
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}
			</form>

			{/* Partner Activation Confirmation Dialog */}
			<Dialog
				open={activateDialogOpen}
				onClose={() => setActivateDialogOpen(false)}
				aria-labelledby="activate-dialog-title"
				aria-describedby="activate-dialog-description"
			>
				<DialogTitle id="activate-dialog-title">Activate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="activate-dialog-description">
						Are you sure you want to activate this partner? This will enable integration
						capabilities and make the partner&apos;s API token functional.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setActivateDialogOpen(false)}
						className={styles.dialogBackButton}
						color="primary"
					>
						Cancel
					</Button>
					<Button
						onClick={confirmActivatePartner}
						color="primary"
						className={styles.dialogBackButton}
						autoFocus
					>
						Activate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Partner Deactivation Confirmation Dialog */}
			<Dialog
				open={deactivateDialogOpen}
				onClose={() => setDeactivateDialogOpen(false)}
				aria-labelledby="deactivate-dialog-title"
				aria-describedby="deactivate-dialog-description"
			>
				<DialogTitle id="deactivate-dialog-title">Deactivate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="deactivate-dialog-description">
						Are you sure you want to deactivate this partner? This will disable all integration
						capabilities and make the partner&apos;s API token non-functional. Any ongoing
						integrations will be stopped.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeactivateDialogOpen(false)}
						className={styles.dialogBackButton}
						color="primary"
					>
						Cancel
					</Button>
					<Button
						onClick={confirmDeactivatePartner}
						className={styles.dialogBackButton}
						color="error"
						autoFocus
					>
						Deactivate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Workplace Unassignment Confirmation Dialog */}
			<Dialog
				open={workplaceUnassignDialogOpen}
				onClose={() => {
					setWorkplaceUnassignDialogOpen(false);
					setPendingWorkplaceId(null);
				}}
				aria-labelledby="workplace-unassign-dialog-title"
				aria-describedby="workplace-unassign-dialog-description"
			>
				<DialogTitle id="workplace-unassign-dialog-title">Unassign Workplace</DialogTitle>
				<DialogContent>
					<DialogContentText id="workplace-unassign-dialog-description">
						Are you sure you want to unassign &quot;
						{workplaces.find((w) => w.id === pendingWorkplaceId)?.name}&quot; from this partner?
						This will remove the partner&apos;s access to this workplace and may affect ongoing
						integrations.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setWorkplaceUnassignDialogOpen(false);
							setPendingWorkplaceId(null);
						}}
						className={styles.dialogBackButton}
						color="primary"
					>
						Cancel
					</Button>
					<Button
						onClick={confirmWorkplaceUnassign}
						className={styles.dialogBackButton}
						color="warning"
						autoFocus
					>
						Unassign Workplace
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default EditPartnerSettings;
